import { z } from "zod";
// The wire shapes of Phase 11 payments (contract `docs/architecture/milestone-e-payments-contract.md`, sections 4.3, 6.2, 7.3
// and 7.4). FROZEN at the P1 contract commit (CHT-1971): the paths, body keys and DTO keys below do not change without a
// reviewed edit of the contract.
//
// Rules that hold for every schema in this file:
// - Money is an integer number of paise (`amountMinor`) and the currency is `INR`. No float.
// - A request body is a STRICT object. It never carries `amount`, `amountMinor` (except the refund body: staff reduce money
//   moved, bounded by the paid amount), `currency`, `price`, `status`, `providerOrderId`, `fingerprint` or `expiresAt`. An
//   unknown key fails the parse and the route answers `400 validation_failed` with no row written.
// - Every FAKE result carries `evidence: "FAKE"`. Nothing here is launch evidence unless it says `REAL`.
// - No secret, signature, payload or customer contact detail appears in any shape.
const id = z.string().min(1).max(64);
const code = z.string().min(1).max(64);
// A JSON instant: ISO 8601 text on the wire, a `Date` in the program.
const instant = z.coerce.date();
const amountMinor = z.number().int().nonnegative();
export const PAYMENT_ROUTE_PATHS = Object.freeze({
    customerCheckout: "/customer/payment-requests/:paymentRequestId/checkout",
    customerCancel: "/customer/payment-requests/:paymentRequestId/cancel",
    webhook: "/webhooks/payments",
    fakeCheckout: "/fake-payments/checkout",
    fakeScenarios: "/fake-payments/scenarios",
    fakeFlush: "/fake-payments/deliveries/flush",
    fakeState: "/fake-payments/state",
    staffList: "/staff/payment-requests",
    staffDetail: "/staff/payment-requests/:paymentRequestId",
    staffReconcile: "/staff/payment-requests/:paymentRequestId/reconcile",
    staffReconcileWindow: "/staff/payments/reconcile-window",
    staffRefund: "/staff/payment-requests/:paymentRequestId/refunds",
});
/** Whose money moved: the fake gateway (FAKE) or the real provider (REAL). `NONE`: no provider order exists yet. */
export const paymentEvidenceLabels = Object.freeze(["FAKE", "REAL", "NONE"]);
// --- customer checkout (section 6.2) ----------------------------------------------------------------------------------------
/** Always `{}`: the amount, currency and order come from the server. A key here is `400 validation_failed`. */
export const customerCheckoutBodySchema = z.strictObject({});
/**
 * The refusals of the checkout route. `409` carries `{ code, currentStatus }`, except `payment.order_pending` (`202`: the
 * provider call is ambiguous, retry later) and `payment.provider_unavailable` (`503`).
 */
export const paymentCheckoutErrorCodes = Object.freeze([
    "payment.not_payable",
    "payment.request_withdrawn",
    "payment.source_changed",
    "payment.provider_changed",
    "payment.checkout_disabled",
    "payment.order_pending",
    "payment.provider_unavailable",
]);
export const customerCheckoutViewSchema = z.strictObject({
    paymentRequestId: id,
    /** Integer paise, read from the stored request. For display only: the launcher never sends an amount to the API. */
    amountMinor,
    currency: z.literal("INR"),
    description: z.string(),
    expiresAt: instant,
    checkout: z.strictObject({
        launcher: z.enum(["FAKE", "NATIVE"]),
        providerOrderId: z.string().min(1).max(128),
        /** The public key id for the native SDK; null for the FAKE launcher. Never a secret. */
        publicKeyId: z.string().min(1).max(128).nullable(),
        merchantDisplayName: z.literal("Lafayette"),
    }),
    /** `FAKE` when the provider is `fake_gateway`. */
    evidence: z.enum(["FAKE", "REAL"]),
});
/** The body of a checkout refusal answer. */
export const paymentCheckoutErrorSchema = z.strictObject({
    code: z.enum(paymentCheckoutErrorCodes),
    currentStatus: code,
});
/**
 * The body of a checkout refusal (`409`, `503`): the usual error envelope (`error.code`, `message`, `requestId`, `correlationId`) with
 * one more key, `payment`, that holds the payment code and the current customer status. A client reads `payment.code`.
 */
export const paymentCheckoutRefusalEnvelopeSchema = z.object({
    error: z.object({ code: code, message: z.string() }),
    payment: paymentCheckoutErrorSchema,
    requestId: z.string(),
    correlationId: z.string(),
});
/** The body of `202 payment.order_pending`: `data` holds the code and the current customer status. */
export const paymentCheckoutPendingEnvelopeSchema = z.object({
    data: paymentCheckoutErrorSchema,
    requestId: z.string(),
    correlationId: z.string(),
});
// --- fake gateway (section 4.3) -----------------------------------------------------------------------------------------------
export const fakeGatewayCheckoutActions = Object.freeze([
    "PAY",
    "FAIL_ATTEMPT",
    "DISMISS",
]);
export const fakeGatewayCheckoutBodySchema = z.strictObject({
    paymentRequestId: id,
    action: z.enum(fakeGatewayCheckoutActions),
});
/** The ADVISORY client result of the fake sheet. It never carries a state: the canonical state comes from the webhook. */
export const fakeGatewayCheckoutResultSchema = z.strictObject({
    outcome: z.enum(["CLIENT_SUCCESS", "CLIENT_FAILED", "DISMISSED"]),
    providerPaymentId: z.string().min(1).max(128).nullable(),
    evidence: z.literal("FAKE"),
});
/** The closed set of scenarios a supervisor can arm on the fake gateway (contract section 4.3). Nothing else is accepted. */
export const fakeGatewayScenarios = Object.freeze([
    "DELIVERY_NOW",
    "DELIVERY_DUPLICATE",
    "DELIVERY_HOLD",
    "DELIVERY_LOSE",
    "DELIVERY_OUT_OF_ORDER",
    "PAY_WRONG_AMOUNT",
    "PAY_WRONG_CURRENCY",
    "NEXT_CREATE_OUTAGE",
    "NEXT_CREATE_TIMEOUT_CREATED",
    "NEXT_READ_OUTAGE",
    "ORDER_FAILED",
    "ORDER_EXPIRED",
    "FORGE_WRONG_ORDER",
    "FORGE_UNKNOWN_ORDER",
    "FORGE_BAD_SIGNATURE",
]);
export const fakeGatewayScenarioBodySchema = z.strictObject({
    paymentRequestId: id,
    scenario: z.enum(fakeGatewayScenarios),
});
export const fakeGatewayScenarioResultSchema = z.strictObject({
    scenario: z.enum(fakeGatewayScenarios),
    /** How many events were queued for delivery to the real webhook route by this call. */
    deliveriesQueued: z.number().int().min(0),
    evidence: z.literal("FAKE"),
});
/** `paymentRequestId: null` flushes the held events of every order. */
export const fakeGatewayFlushBodySchema = z.strictObject({ paymentRequestId: id.nullable() });
export const fakeGatewayFlushResultSchema = z.strictObject({
    deliveriesQueued: z.number().int().min(0),
    evidence: z.literal("FAKE"),
});
/** Ids, amounts and statuses of the fake world. No customer data, no secret. */
export const fakeGatewayStateSchema = z.strictObject({
    provider: z.string(),
    orders: z.array(z.strictObject({
        providerOrderId: z.string(),
        merchantReference: z.string(),
        amountMinor: z.number().int(),
        status: z.string(),
        expiresAt: z.string(),
    })),
    payments: z.array(z.strictObject({
        providerPaymentId: z.string(),
        providerOrderId: z.string(),
        amountMinor: z.number().int(),
        currency: z.string(),
        status: z.string(),
    })),
    refunds: z.array(z.strictObject({
        providerRefundId: z.string(),
        providerPaymentId: z.string(),
        amountMinor: z.number().int(),
        status: z.string(),
    })),
    held: z.array(z.strictObject({
        eventId: z.string(),
        kind: z.string(),
        providerOrderId: z.string().nullable(),
    })),
    evidence: z.literal("FAKE"),
});
// --- staff list and detail (section 7.3) --------------------------------------------------------------------------------------
export const staffPaymentListFilters = Object.freeze([
    "READY",
    "PENDING",
    "PAID",
    "FAILED",
    "EXPIRED",
    "CANCELLED",
    "REFUND_PENDING",
    "REFUNDED",
    "NEEDS_RECONCILIATION",
]);
export const staffPaymentListFilterSchema = z.enum(staffPaymentListFilters);
export const STAFF_PAYMENT_LIST_MAX_LIMIT = 100;
export const staffPaymentListQuerySchema = z.strictObject({
    filter: staffPaymentListFilterSchema.optional(),
    cursor: z.string().min(1).max(512).optional(),
    limit: z.coerce.number().int().min(1).max(STAFF_PAYMENT_LIST_MAX_LIMIT).optional(),
});
export const staffPaymentListRowSchema = z.strictObject({
    id,
    customerDisplayName: z.string(),
    status: code,
    amountMinor,
    currency: z.literal("INR"),
    sourceKind: z.enum(["FIXED_PRICE", "CONCIERGE_QUOTE"]),
    conciergeRequestId: id.nullable(),
    expiresAt: instant,
    statusChangedAt: instant,
    hasOpenException: z.boolean(),
    hasOpenProviderCall: z.boolean(),
    hasRequestedRefund: z.boolean(),
    evidence: z.enum(paymentEvidenceLabels),
});
export const staffPaymentListSchema = z.strictObject({
    rows: z.array(staffPaymentListRowSchema),
    /** Keyset on `(status_changed_at, id)`. `null`: no further page. */
    nextCursor: z.string().min(1).max(512).nullable(),
});
// A provider call as staff read it: ids, kind, state and codes. No payload, no signature, no key.
export const staffPaymentProviderCallSchema = z.strictObject({
    id,
    kind: z.enum(["CREATE_ORDER", "CREATE_REFUND"]),
    refundId: id.nullable(),
    attempt: z.number().int().positive(),
    provider: code,
    amountMinor,
    currency: z.literal("INR"),
    state: z.enum(["CALLING", "AMBIGUOUS", "SUCCEEDED", "REJECTED"]),
    providerObjectId: z.string().min(1).max(128).nullable(),
    outcomeCode: z.string().max(64).nullable(),
    errorClass: z.enum(["RETRYABLE", "AMBIGUOUS", "TERMINAL"]).nullable(),
    startedAt: instant,
    finishedAt: instant.nullable(),
});
// Where the amount came from (the fingerprint chain, section 2.2).
export const staffPaymentAmountSourceSchema = z.strictObject({
    sourceType: z.enum(["CATALOG_FIXED_PRICE", "CONCIERGE_QUOTE"]),
    sourceId: id,
    sourceVersion: z.number().int().positive(),
    amountMinor,
    currency: z.literal("INR"),
    fingerprint: z.string().regex(/^[0-9a-f]{64}$/u),
    resolvedAt: instant,
});
export const staffPaymentReconciliationRunSchema = z.strictObject({
    id,
    scope: z.enum(["PAYMENT", "WINDOW", "EXPIRY_SWEEP"]),
    state: z.enum(["RUNNING", "DONE", "FAILED"]),
    examined: z.number().int().nonnegative(),
    applied: z.number().int().nonnegative(),
    duplicates: z.number().int().nonnegative(),
    exceptions: z.number().int().nonnegative(),
    deferred: z.number().int().nonnegative(),
    foreign: z.number().int().nonnegative(),
    errorClass: z.string().max(64).nullable(),
    startedAt: instant,
    finishedAt: instant.nullable(),
});
const staffRecord = z.record(z.string(), z.unknown());
/** The existing staff payment view plus the Phase 11 sections. The top level is strict. */
export const staffPaymentDetailSchema = z.strictObject({
    request: staffRecord,
    quote: staffRecord.nullable(),
    refunds: z.array(staffRecord),
    observations: z.array(staffRecord),
    exceptions: z.array(staffRecord),
    transitions: z.array(staffRecord),
    providerCalls: z.array(staffPaymentProviderCallSchema),
    amountSource: staffPaymentAmountSourceSchema.nullable(),
    reconciliationRuns: z.array(staffPaymentReconciliationRunSchema),
    evidence: z.enum(paymentEvidenceLabels),
});
// --- staff commands (section 7.4) -------------------------------------------------------------------------------------------------
/** The window of a reconcile-by-window run: at most 24 hours (the route checks). `from` inclusive, `to` exclusive. */
export const staffReconcileWindowBodySchema = z.strictObject({
    from: instant,
    to: instant,
});
export const paymentRefundReasonCodeValues = Object.freeze([
    "CUSTOMER_REQUEST",
    "SUPPLIER_UNAVAILABLE",
    "SERVICE_RECOVERY",
    "DUPLICATE_PAYMENT",
    "OTHER",
]);
/**
 * The ONE staff-entered amount in the system. It can only reduce money moved: the domain check and the database rule LF021
 * bound it by the remaining refundable amount. The `Idempotency-Key` header carries the idempotency key, not the body.
 */
export const staffRefundBodySchema = z.strictObject({
    amountMinor: z.number().int().positive(),
    reasonCode: z.enum(paymentRefundReasonCodeValues),
    reason: z.string().trim().min(1).max(500),
});
/** The outcome of a reconcile command: flat ids, counts and codes. */
export const staffReconcileOutcomeSchema = z.strictObject({
    paymentRequestId: id.nullable(),
    runId: id,
    /** `FAILED`: the provider could not answer. Nothing was applied from a partial answer; the caller may retry. */
    state: z.enum(["DONE", "FAILED"]),
    errorClass: z.string().max(64).nullable(),
    examined: z.number().int().nonnegative(),
    applied: z.number().int().nonnegative(),
    duplicates: z.number().int().nonnegative(),
    exceptions: z.number().int().nonnegative(),
    deferred: z.number().int().nonnegative(),
    foreign: z.number().int().nonnegative(),
    evidence: z.enum(paymentEvidenceLabels),
});
/**
 * The answer of the refund command. `SENT_TO_PROVIDER`: the provider has the refund and it is still REQUESTED; only a settled-refund
 * observation (a webhook or a reconcile) settles it. `ALREADY_FINAL`: a retry that found the refund settled or failed. `FAILED`: the
 * provider refused, the refund failed and its amount is free again. An ambiguous or unreachable provider is a `202` or `503` with a code.
 */
export const staffRefundResultSchema = z.strictObject({
    refundId: id,
    outcome: z.enum(["SENT_TO_PROVIDER", "ALREADY_FINAL", "FAILED"]),
    status: z.enum(["REQUESTED", "SETTLED", "FAILED"]),
    /** A closed code for `FAILED`; `null` otherwise. */
    code: z.string().max(64).nullable(),
    evidence: z.enum(["FAKE", "REAL", "NONE"]),
});
/** The `202` of a refund whose provider call may be in flight, and the codes of the other refund answers. */
export const paymentRefundAnswerCodes = Object.freeze([
    "payment.refund_pending",
    "payment.refund_disabled",
    "payment.provider_unavailable",
]);
// --- customer cancel (CHT-756 review fix) -----------------------------------------------------------------------------------------
/** Always `{}`: the request is named by the path. A key here is `400`. */
export const customerCancelBodySchema = z.strictObject({});
/**
 * The refusals of the customer cancel route (`409`, all with `{ code, currentStatus }`): `payment.not_cancellable` (not READY or
 * PENDING_PAYMENT: paid, ended), `payment.order_pending` (an order call is in flight) and `payment.settlement_possible` (the provider
 * cannot say there was no settlement, or an open exception may hold a capture).
 */
export const paymentCancelErrorCodes = Object.freeze([
    "payment.not_cancellable",
    "payment.order_pending",
    "payment.settlement_possible",
]);
export const paymentCancelErrorSchema = z.strictObject({
    code: z.enum(paymentCancelErrorCodes),
    currentStatus: code,
});
/** The body of a cancel refusal: the usual error envelope with one more key, `payment`. */
export const paymentCancelRefusalEnvelopeSchema = z.object({
    error: z.object({ code: code, message: z.string() }),
    payment: paymentCancelErrorSchema,
    requestId: z.string(),
    correlationId: z.string(),
});
